import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccount, useBalance, useChainId, useChains, useSwitchChain } from 'wagmi';
import { getWalletClient, switchChain as switchChainAction } from '@wagmi/core';
import { EVM, convertQuoteToRoute, createConfig as createLifiConfig, executeRoute, getQuote } from '@lifi/sdk';
import { JsonRpcProvider } from 'ethers';
import { encodeFunctionData, type Address } from 'viem';
import { wagmiConfig } from '../wagmiConfig';
import { getChainIconSrc } from '../config/chainDisplay';
import { brand, storageKeys } from '../config/brand';
import { factoryAbi } from '../abi/factory';
import { getFactoryAddress } from '../config/factories';

type GasRow = { id: string; label: string; gasUnits: string };

type LifiApiChain = {
  id: number;
  name: string;
  coin?: string;
  logoURI?: string;
  chainType?: string;
  mainnet?: boolean;
  /** If false, chain is injected locally (no LI.FI gas/bridge); use RPC + optional CoinGecko. */
  lifiSupported?: boolean;
  metamask?: {
    rpcUrls?: string[];
    nativeCurrency?: {
      symbol?: string;
      decimals?: number;
    };
  };
  nativeToken?: {
    symbol?: string;
    decimals?: number;
    priceUSD?: string;
  };
};

const PULSECHAIN: LifiApiChain = {
  id: 369,
  name: 'PulseChain',
  coin: 'PLS',
  chainType: 'EVM',
  mainnet: true,
  lifiSupported: false,
  logoURI: getChainIconSrc(369),
  metamask: {
    rpcUrls: ['https://rpc.pulsechain.com'],
    nativeCurrency: { symbol: 'PLS', decimals: 18 },
  },
  nativeToken: { symbol: 'PLS', decimals: 18 },
};

/** CoinGecko `ids` for native token USD when LI.FI omits `priceUSD`. */
const NATIVE_COINGECKO_ID: Record<number, string> = {
  369: 'pulsechain',
};
const SELECTED_CHAINS_STORAGE_KEY = storageKeys.gasEstimatorChains;
const LIFI_INTEGRATOR_FEE = 0.02; // 2%

type LifiState = {
  loading: boolean;
  error?: string;
  routeId?: string;
  success?: string;
  routeSummary?: string;
};

type PendingRoute = {
  chainId: number;
  chainName: string;
  route: any;
};

type GasTier = 'standard' | 'fast' | 'fastest';
type LifiGasPrice = {
  standard?: number;
  fast?: number;
  fastest?: number;
  lastUpdated?: number;
};

let lifiConfigured = false;
function ensureLifiConfig() {
  if (lifiConfigured) return;
  createLifiConfig({
    integrator: brand.lifiIntegrator,
    apiKey: process.env.REACT_APP_LIFI_API_KEY,
    routeOptions: {
      fee: LIFI_INTEGRATOR_FEE,
    },
    providers: [
      EVM({
        getWalletClient: async () => getWalletClient(wagmiConfig),
        switchChain: async (chainId: number) => {
          const chain = await switchChainAction(wagmiConfig, { chainId: chainId as any });
          return getWalletClient(wagmiConfig, { chainId: chain.id });
        },
      }),
    ],
  });
  lifiConfigured = true;
}

function toBaseUnits(amount: number, decimals: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return '0';
  const fixed = amount.toFixed(Math.min(decimals, 12));
  const [whole, frac = ''] = fixed.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  return `${whole}${padded}`.replace(/^0+/, '') || '0';
}

function formatTokenAmount(rawAmount: string | undefined, decimals: number | undefined): string {
  if (!rawAmount) return '—';
  if (!decimals && decimals !== 0) return '—';
  try {
    const n = Number(rawAmount) / 10 ** decimals;
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
  } catch {
    return '—';
  }
}

function emptyRow(i: number): GasRow {
  return { id: `r-${Date.now()}-${i}`, label: '', gasUnits: '' };
}

function fmtUsd(v: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(v);
}

/** LI.FI `GET /v1/gas/prices/{chainId}` returns gas price per unit gas in wei (smallest native unit). */
function lifiGasWeiToGwei(wei: number): number {
  return wei / 1e9;
}

async function fetchGasWeiTiersFromRpc(rpcUrl: string): Promise<LifiGasPrice | null> {
  try {
    const provider = new JsonRpcProvider(rpcUrl);
    const feeData = await provider.getFeeData();
    const wei = feeData.gasPrice ?? feeData.maxFeePerGas ?? feeData.maxPriorityFeePerGas;
    if (wei == null) return null;
    const base = Number(wei);
    if (!Number.isFinite(base) || base <= 0) return null;
    return {
      standard: base,
      fast: base * 1.1,
      fastest: base * 1.2,
    };
  } catch {
    return null;
  }
}

function mergeChainsFromLifi(list: LifiApiChain[]): LifiApiChain[] {
  const hasPulse = list.some((c) => c.id === PULSECHAIN.id);
  const merged = hasPulse ? list : [...list, PULSECHAIN];
  return merged.sort((a, b) => a.name.localeCompare(b.name));
}

function gasCurrencyMeta(chain: LifiApiChain): { symbol: string; decimals: number } {
  // For gas funding we trust chain-native currency metadata only.
  const symbol = chain.metamask?.nativeCurrency?.symbol ?? chain.coin ?? 'NATIVE';
  // Hyperliquid/Stable currently return LI.FI gas values that behave as 18-decimal EVM gas units.
  // Using 6 here leads to massively inflated native amounts.
  if (chain.name === 'Hyperliquid' || chain.name === 'Stable') {
    return { symbol, decimals: 18 };
  }
  const decimals = chain.metamask?.nativeCurrency?.decimals ?? 18;
  return { symbol, decimals };
}

function toFriendlyLifiError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? 'Route execution failed.');
  const lower = raw.toLowerCase();

  if (
    lower.includes('user rejected') ||
    lower.includes('user denied') ||
    lower.includes('transaction rejected') ||
    lower.includes('cancelled') ||
    lower.includes('canceled')
  ) {
    return 'Transaction cancelled in wallet.';
  }

  if (
    lower.includes('bundle id is unknown') ||
    lower.includes('no matching bundle found') ||
    lower.includes('has not been submitted')
  ) {
    return 'Route was not submitted.';
  }

  return raw;
}

function summarizeRouteSteps(route: any): string | undefined {
  const steps = Array.isArray(route?.steps) ? route.steps : [];
  if (steps.length === 0) return undefined;
  const labels = steps
    .map((s: any) => {
      if (s?.toolDetails?.name) return s.toolDetails.name as string;
      if (s?.type) return s.type as string;
      if (s?.action?.fromChainId || s?.action?.toChainId) {
        return `${s?.action?.fromChainId ?? '?'}->${s?.action?.toChainId ?? '?'}`;
      }
      return null;
    })
    .filter((v: unknown): v is string => typeof v === 'string' && v.length > 0);
  if (labels.length === 0) return `Route has ${steps.length} step(s)`;
  return labels.slice(0, 3).join(' -> ') + (labels.length > 3 ? ' -> ...' : '');
}

function normalizeHexBytecode(input: string): { ok: true; value: `0x${string}`; bytes: number } | { ok: false; error: string } {
  const compact = input.trim().replace(/\s+/g, '');
  if (!compact) return { ok: false, error: 'Paste contract bytecode to estimate deploy gas.' };
  if (!compact.startsWith('0x')) return { ok: false, error: 'Bytecode must start with 0x.' };
  const hex = compact.slice(2);
  if (hex.length === 0) return { ok: false, error: 'Bytecode is empty.' };
  if (!/^[0-9a-fA-F]+$/.test(hex)) return { ok: false, error: 'Bytecode contains non-hex characters.' };
  if (hex.length % 2 !== 0) return { ok: false, error: 'Bytecode hex length must be even.' };
  return { ok: true, value: `0x${hex}` as `0x${string}`, bytes: hex.length / 2 };
}

const ETH_ESTIMATE_RPC_FALLBACK = 'https://ethereum-rpc.publicnode.com';

export function GasEstimator() {
  ensureLifiConfig();

  const { address, isConnected } = useAccount();
  const sourceChainId = useChainId();
  const walletChains = useChains();
  const { switchChainAsync } = useSwitchChain();
  const { data: nativeBalance } = useBalance({
    address,
    chainId: sourceChainId,
    query: { enabled: Boolean(address && sourceChainId) },
  });

  const [rows, setRows] = useState<GasRow[]>([
    { id: 'splitter', label: 'Create splitter', gasUnits: '1140000' },
  ]);
  const [chains, setChains] = useState<LifiApiChain[]>([]);
  const [chainsLoading, setChainsLoading] = useState(false);
  const [chainsError, setChainsError] = useState('');
  const [openChains, setOpenChains] = useState(false);
  const [selectedChainIds, setSelectedChainIds] = useState<number[]>([]);
  const [chainInputs, setChainInputs] = useState<Record<string, { gasPriceGwei: string; nativeUsd: string }>>({});
  const [gasTier, setGasTier] = useState<GasTier>('standard');
  const [gasByChain, setGasByChain] = useState<Record<string, LifiGasPrice>>({});
  const [bufferMultiplierInput, setBufferMultiplierInput] = useState('2');
  const [refreshingLive, setRefreshingLive] = useState(false);
  const [liveStatus, setLiveStatus] = useState('');
  const [lifiState, setLifiState] = useState<Record<string, LifiState>>({});
  const [pendingRoute, setPendingRoute] = useState<PendingRoute | null>(null);
  const [bytecodeInput, setBytecodeInput] = useState('');
  const [estimateGasLoading, setEstimateGasLoading] = useState(false);
  const [estimateGasStatus, setEstimateGasStatus] = useState('');
  const [splitterDeployEstimateGas, setSplitterDeployEstimateGas] = useState<string>('');
  const [splitterDeployEstimateLoading, setSplitterDeployEstimateLoading] = useState(false);

  useEffect(() => {
    const loadChains = async () => {
      setChainsLoading(true);
      setChainsError('');
      try {
        const headers: Record<string, string> = {};
        if (process.env.REACT_APP_LIFI_API_KEY) {
          headers['x-lifi-api-key'] = process.env.REACT_APP_LIFI_API_KEY;
        }

        const res = await fetch('https://li.quest/v1/chains?chainTypes=EVM', { headers });
        if (!res.ok) {
          throw new Error(`LI.FI chains request failed (${res.status})`);
        }

        const data = (await res.json()) as { chains?: LifiApiChain[] };
        const evmMainnet = (data.chains ?? []).filter((c) => c.chainType === 'EVM' && c.mainnet !== false);
        setChains(mergeChainsFromLifi(evmMainnet));
      } catch (err) {
        setChainsError(err instanceof Error ? err.message : 'Failed to load LI.FI chains.');
      } finally {
        setChainsLoading(false);
      }
    };

    loadChains();
  }, []);

  useEffect(() => {
    if (chains.length === 0) return;

    const storedRaw = typeof window !== 'undefined' ? window.localStorage.getItem(SELECTED_CHAINS_STORAGE_KEY) : null;
    let storedIds: number[] = [];
    if (storedRaw) {
      try {
        const parsed = JSON.parse(storedRaw) as unknown;
        if (Array.isArray(parsed)) {
          storedIds = parsed.map((v) => Number(v)).filter((v) => Number.isInteger(v));
        }
      } catch {
        storedIds = [];
      }
    }

    setSelectedChainIds((prev) => {
      if (prev.length > 0) {
        return prev.filter((id) => chains.some((c) => c.id === id));
      }
      if (storedIds.length > 0) {
        const fromStorage = storedIds.filter((id) => chains.some((c) => c.id === id));
        if (fromStorage.length > 0) return fromStorage;
      }

      const preferred = [1, 56, 8453, 42161, 137, 43114, 10, 100, 130, 59144, 143, 534352, 369];
      const picked = preferred.filter((id) => chains.some((c) => c.id === id));
      return picked.length > 0 ? picked : chains.slice(0, 6).map((c) => c.id);
    });

    setChainInputs((prev) => {
      const next = { ...prev };
      for (const chain of chains) {
        const key = String(chain.id);
        if (!next[key]) {
          const { symbol } = gasCurrencyMeta(chain);
          const canUseNativeTokenUsd =
            Boolean(chain.nativeToken?.symbol) &&
            chain.nativeToken?.symbol?.toUpperCase() === symbol.toUpperCase();
          next[key] = {
            gasPriceGwei: '',
            nativeUsd: canUseNativeTokenUsd ? chain.nativeToken?.priceUSD ?? '' : '',
          };
        }
      }
      return next;
    });
  }, [chains]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SELECTED_CHAINS_STORAGE_KEY, JSON.stringify(selectedChainIds));
  }, [selectedChainIds]);

  useEffect(() => {
    if (Object.keys(gasByChain).length === 0) return;
    setChainInputs((prev) => {
      const next = { ...prev };
      for (const chainId of selectedChainIds) {
        const key = String(chainId);
        const quote = gasByChain[key];
        const tierWei = quote?.[gasTier];
        if (tierWei !== undefined && Number.isFinite(tierWei) && tierWei > 0) {
          const gwei = lifiGasWeiToGwei(tierWei);
          next[key] = {
            ...next[key],
            gasPriceGwei: String(Number(gwei.toPrecision(8))),
          };
        }
      }
      return next;
    });
  }, [gasTier, gasByChain, selectedChainIds]);

  const bufferMultiplier = useMemo(() => {
    const n = Number(bufferMultiplierInput);
    if (!Number.isFinite(n) || n < 1) return 1;
    return n;
  }, [bufferMultiplierInput]);

  const totalGasUnits = useMemo(() => {
    let total = 0;
    for (const row of rows) {
      const n = Number(row.gasUnits);
      if (Number.isFinite(n) && n > 0) total += n;
    }
    return total;
  }, [rows]);
  const selectedChains = useMemo(
    () => chains.filter((c) => selectedChainIds.includes(c.id)),
    [chains, selectedChainIds]
  );
  const walletChainMeta = useMemo(
    () => walletChains.find((c) => c.id === sourceChainId),
    [walletChains, sourceChainId]
  );
  const walletChainName = walletChainMeta?.name ?? (sourceChainId ? `Chain ${sourceChainId}` : 'Not connected');
  const walletChainIcon = sourceChainId ? getChainIconSrc(sourceChainId) : undefined;

  const estimates = useMemo(() => {
    return selectedChains.map((chain) => {
      const key = String(chain.id);
      const input = chainInputs[key] ?? { gasPriceGwei: '', nativeUsd: '' };
      const gasPriceGwei = Number(input.gasPriceGwei);
      const nativeUsd = Number(input.nativeUsd);
      const { decimals } = gasCurrencyMeta(chain);
      // Table gas column is gwei per gas; on-chain cost uses wei/gas × gas limit.
      const costWei = totalGasUnits * gasPriceGwei * 1e9 * bufferMultiplier;
      const nativeAmount = costWei / 10 ** decimals;
      const usdAmount = nativeAmount * nativeUsd;

      return { chain, gasPriceGwei, nativeUsd, nativeAmount, usdAmount };
    });
  }, [selectedChains, chainInputs, totalGasUnits, bufferMultiplier]);
  const totalEstimatedUsd = useMemo(
    () => estimates.reduce((sum, e) => (Number.isFinite(e.usdAmount) ? sum + e.usdAmount : sum), 0),
    [estimates]
  );

  const updateRow = (id: string, patch: Partial<GasRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow(prev.length)]);

  const applyEstimatedDeployGas = useCallback((gasUnits: string) => {
    setRows((prev) => {
      const withoutDerived = prev.filter((r) => r.id !== 'bytecode-deploy');
      return [...withoutDerived, { id: 'bytecode-deploy', label: 'Deploy from bytecode', gasUnits }];
    });
  }, []);

  const estimateDeployGasFromBytecode = useCallback(async () => {
    setEstimateGasStatus('');
    const parsed = normalizeHexBytecode(bytecodeInput);
    if (!parsed.ok) {
      setEstimateGasStatus(parsed.error);
      return;
    }
    if (!sourceChainId) {
      setEstimateGasStatus('Connect wallet and select a source chain first.');
      return;
    }
    const chainMeta = walletChains.find((c) => c.id === sourceChainId);
    const rpcUrl = sourceChainId === 1 ? ETH_ESTIMATE_RPC_FALLBACK : chainMeta?.rpcUrls?.default?.http?.[0];
    if (!rpcUrl) {
      setEstimateGasStatus(`No RPC URL available for chain ${sourceChainId}.`);
      return;
    }

    setEstimateGasLoading(true);
    try {
      const provider = new JsonRpcProvider(rpcUrl);
      const txReq: { from?: string; data: string } = {
        data: parsed.value,
      };
      if (address) txReq.from = address;
      const estimate = await provider.estimateGas(txReq);
      const gasUnits = estimate.toString();
      applyEstimatedDeployGas(gasUnits);
      setEstimateGasStatus(
        `Estimated ${gasUnits} gas on ${chainMeta?.name ?? `chain ${sourceChainId}`} from ${parsed.bytes.toLocaleString()} bytes of bytecode.`
      );
    } catch {
      setEstimateGasStatus('calculation failed');
    } finally {
      setEstimateGasLoading(false);
    }
  }, [address, applyEstimatedDeployGas, bytecodeInput, sourceChainId, walletChains]);

  useEffect(() => {
    let cancelled = false;

    const estimateFactoryCreateSplitterGas = async () => {
      if (!sourceChainId || !address) {
        setSplitterDeployEstimateGas('');
        return;
      }
      const factory = getFactoryAddress(sourceChainId);
      if (!factory) {
        setSplitterDeployEstimateGas('');
        return;
      }
      const chainMeta = walletChains.find((c) => c.id === sourceChainId);
      const rpcUrl = sourceChainId === 1 ? ETH_ESTIMATE_RPC_FALLBACK : chainMeta?.rpcUrls?.default?.http?.[0];
      if (!rpcUrl) {
        setSplitterDeployEstimateGas('');
        return;
      }

      setSplitterDeployEstimateLoading(true);
      try {
        const provider = new JsonRpcProvider(rpcUrl);
        const owner = address as Address;
        const data = encodeFunctionData({
          abi: factoryAbi,
          functionName: 'createSplitter',
          args: [[owner], [BigInt(100)], BigInt(1000), owner],
        });
        const estimate = await provider.estimateGas({
          from: owner,
          to: factory,
          data,
        });
        if (!cancelled) setSplitterDeployEstimateGas(estimate.toString());
      } catch {
        if (!cancelled) setSplitterDeployEstimateGas('');
      } finally {
        if (!cancelled) setSplitterDeployEstimateLoading(false);
      }
    };

    estimateFactoryCreateSplitterGas();
    return () => {
      cancelled = true;
    };
  }, [address, sourceChainId, walletChains]);

  const toggleChain = (id: number) => {
    setSelectedChainIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const selectAllChains = () => setSelectedChainIds(chains.map((c) => c.id));
  const deselectAllChains = () => setSelectedChainIds([]);

  const fetchLivePricing = async () => {
    setRefreshingLive(true);
    setLiveStatus('');
    const headers: Record<string, string> = {};
    if (process.env.REACT_APP_LIFI_API_KEY) {
      headers['x-lifi-api-key'] = process.env.REACT_APP_LIFI_API_KEY;
    }

    const gasUpdates = await Promise.all(
      selectedChains.map(async (chain) => {
        if (chain.lifiSupported === false) {
          const rpc = chain.metamask?.rpcUrls?.[0];
          if (!rpc) return { chainId: chain.id, gas: null as LifiGasPrice | null };
          const gas = await fetchGasWeiTiersFromRpc(rpc);
          return { chainId: chain.id, gas };
        }
        try {
          const res = await fetch(`https://li.quest/v1/gas/prices/${chain.id}`, { headers });
          if (!res.ok) {
            const rpc = chain.metamask?.rpcUrls?.[0];
            if (!rpc) return { chainId: chain.id, gas: null as LifiGasPrice | null };
            const fallback = await fetchGasWeiTiersFromRpc(rpc);
            return { chainId: chain.id, gas: fallback };
          }
          const gas = (await res.json()) as LifiGasPrice;
          const tierWei = gas?.[gasTier];
          if (tierWei !== undefined && Number.isFinite(tierWei) && tierWei > 0) {
            return { chainId: chain.id, gas };
          }
          const rpc = chain.metamask?.rpcUrls?.[0];
          if (!rpc) return { chainId: chain.id, gas: null };
          const fallback = await fetchGasWeiTiersFromRpc(rpc);
          return { chainId: chain.id, gas: fallback };
        } catch {
          const rpc = chain.metamask?.rpcUrls?.[0];
          if (!rpc) return { chainId: chain.id, gas: null as LifiGasPrice | null };
          const fallback = await fetchGasWeiTiersFromRpc(rpc);
          return { chainId: chain.id, gas: fallback };
        }
      })
    );

    setGasByChain((prev) => {
      const next = { ...prev };
      for (const u of gasUpdates) {
        if (u.gas) next[String(u.chainId)] = u.gas;
      }
      return next;
    });

    const coingeckoIds: string[] = [];
    for (const c of selectedChains) {
      const id = NATIVE_COINGECKO_ID[c.id];
      if (id && coingeckoIds.indexOf(id) === -1) coingeckoIds.push(id);
    }
    let coingeckoUsd: Record<string, number> = {};
    if (coingeckoIds.length > 0) {
      try {
        const cgUrl = `https://api.coingecko.com/api/v3/simple/price?ids=${encodeURIComponent(
          coingeckoIds.join(',')
        )}&vs_currencies=usd`;
        const cgRes = await fetch(cgUrl);
        if (cgRes.ok) {
          const cgJson = (await cgRes.json()) as Record<string, { usd?: number }>;
          for (const [k, row] of Object.entries(cgJson)) {
            const n = Number(row?.usd);
            if (Number.isFinite(n) && n > 0) coingeckoUsd[k] = n;
          }
        }
      } catch {
        // keep prior USD inputs
      }
    }

    setChainInputs((prev) => {
      const next = { ...prev };
      for (const chain of selectedChains) {
        const key = String(chain.id);
        const gasWei = gasUpdates.find((g) => g.chainId === chain.id)?.gas?.[gasTier];
        const { symbol } = gasCurrencyMeta(chain);
        const canUseNativeTokenUsd =
          Boolean(chain.nativeToken?.symbol) &&
          chain.nativeToken?.symbol?.toUpperCase() === symbol.toUpperCase();
        const lifiUsd = canUseNativeTokenUsd ? Number(chain.nativeToken?.priceUSD ?? '') : Number.NaN;
        const cgId = NATIVE_COINGECKO_ID[chain.id];
        const cg = cgId ? coingeckoUsd[cgId] : undefined;
        const usd = Number.isFinite(lifiUsd) && lifiUsd > 0 ? lifiUsd : cg;

        next[key] = {
          gasPriceGwei:
            gasWei !== null && gasWei !== undefined && gasWei > 0
              ? String(Number(lifiGasWeiToGwei(gasWei).toPrecision(8)))
              : prev[key]?.gasPriceGwei ?? '',
          nativeUsd:
            usd !== undefined && Number.isFinite(usd) && usd > 0
              ? String(Number(usd.toPrecision(8)))
              : prev[key]?.nativeUsd ?? '',
        };
      }
      return next;
    });

    const gasOk = gasUpdates.filter((g) => g.gas !== null && Number.isFinite(Number(g.gas?.[gasTier]))).length;
    const usdOk = selectedChains.filter((c) => {
      const u = Number(c.nativeToken?.priceUSD ?? '');
      if (Number.isFinite(u) && u > 0) return true;
      const cgId = NATIVE_COINGECKO_ID[c.id];
      return Boolean(cgId && coingeckoUsd[cgId]);
    }).length;
    setLiveStatus(
      `Live refresh complete: gas ${gasOk}/${selectedChains.length}, USD ${usdOk}/${selectedChains.length}.`
    );
    setRefreshingLive(false);
  };

  const executePreparedRoute = async (chainId: number, route: any) => {
    setPendingRoute(null);
    setLifiState((prev) => ({
      ...prev,
      [String(chainId)]: {
        loading: true,
        routeId: prev[String(chainId)]?.routeId,
        error: undefined,
        success: undefined,
        routeSummary: prev[String(chainId)]?.routeSummary,
      },
    }));

    try {
      await executeRoute(route, {
        switchChainHook: async (targetChainId) => {
          await switchChainAsync({ chainId: targetChainId });
          return getWalletClient(wagmiConfig, { chainId: targetChainId });
        },
        updateRouteHook: (updatedRoute) => {
          setLifiState((prev) => ({
            ...prev,
            [String(chainId)]: {
              loading: true,
              routeId: updatedRoute.id,
              routeSummary: summarizeRouteSteps(updatedRoute),
            },
          }));
        },
      });

      setLifiState((prev) => ({
        ...prev,
        [String(chainId)]: {
          loading: false,
          routeId: route.id,
          success: 'Bridge completed successfully.',
          routeSummary: summarizeRouteSteps(route),
        },
      }));
    } catch (err) {
      setLifiState((prev) => ({
        ...prev,
        [String(chainId)]: {
          loading: false,
          routeId: prev[String(chainId)]?.routeId,
          error: toFriendlyLifiError(err),
          success: undefined,
          routeSummary: prev[String(chainId)]?.routeSummary,
        },
      }));
    }
  };

  const executeLifiQuote = async (chainId: number) => {
    const est = estimates.find((e) => e.chain.id === chainId);
    if (!est || !address || !sourceChainId) return;
    if (est.chain.lifiSupported === false) return;

    setLifiState((prev) => ({
      ...prev,
      [String(chainId)]: {
        loading: true,
        routeId: prev[String(chainId)]?.routeId,
        error: undefined,
        success: undefined,
        routeSummary: prev[String(chainId)]?.routeSummary,
      },
    }));

    try {
      const sourceChainMeta = chains.find((c) => c.id === sourceChainId);
      const { decimals: sourceDecimals } = gasCurrencyMeta(sourceChainMeta ?? est.chain);
      const { decimals: destDecimals } = gasCurrencyMeta(est.chain);
      const targetToAmount = toBaseUnits(est.nativeAmount, destDecimals);

      const sourceUsd = Number(chainInputs[String(sourceChainId)]?.nativeUsd);
      const destUsd = Number(chainInputs[String(chainId)]?.nativeUsd);
      const usdRatioGuess =
        Number.isFinite(sourceUsd) && sourceUsd > 0 && Number.isFinite(destUsd) && destUsd > 0
          ? (est.nativeAmount * destUsd) / sourceUsd
          : est.nativeAmount;

      let sourceAmountGuess = Math.max(usdRatioGuess, 0.000001);
      let quote: Awaited<ReturnType<typeof getQuote>> | null = null;

      // getQuote is exact-input; iterate source amount to target desired destination amount.
      for (let i = 0; i < 4; i += 1) {
        const fromAmount = toBaseUnits(sourceAmountGuess, sourceDecimals);
        quote = await getQuote({
          fromAddress: address,
          toAddress: address,
          fromChain: sourceChainId,
          toChain: chainId,
          fromToken: '0x0000000000000000000000000000000000000000',
          toToken: '0x0000000000000000000000000000000000000000',
          fromAmount,
        });

        const receivedRaw = (quote as any)?.estimate?.toAmount ?? (quote as any)?.estimate?.toAmountMin ?? '0';
        const received = BigInt(receivedRaw);
        const target = BigInt(targetToAmount);
        if (received >= target) break;
        if (received <= BigInt(0)) break;

        const receivedNum = Number(received);
        const targetNum = Number(target);
        if (!Number.isFinite(receivedNum) || !Number.isFinite(targetNum) || receivedNum <= 0) break;

        const bump = Math.min(Math.max((targetNum / receivedNum) * 1.03, 1.05), 3);
        sourceAmountGuess *= bump;
      }
      if (!quote) throw new Error('Unable to compute LI.FI quote.');

      const route = convertQuoteToRoute(quote);
      setLifiState((prev) => ({
        ...prev,
        [String(chainId)]: {
          loading: false,
          routeId: route.id,
          error: undefined,
          success: undefined,
          routeSummary: summarizeRouteSteps(route),
        },
      }));
      setPendingRoute({ chainId, chainName: est.chain.name, route });
    } catch (err) {
      setLifiState((prev) => ({
        ...prev,
        [String(chainId)]: {
          loading: false,
          routeId: prev[String(chainId)]?.routeId,
          error: toFriendlyLifiError(err),
          success: undefined,
          routeSummary: prev[String(chainId)]?.routeSummary,
        },
      }));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Multi-chain deployment cost estimator</h1>
        <p className="text-sm opacity-70 mt-1">
          Enter contract gas rows, select target chains, then estimate native and USD needed in your deploy wallet.
        </p>
      </div>

      <div className="card bg-neutral text-neutral-content border border-neutral-700/80">
        <div className="card-body space-y-4">
          <div className="rounded-lg border border-neutral-700/80 bg-base-300/30 p-3 space-y-2">
            <h3 className="font-semibold text-sm">Optional bytecode deploy-gas estimator</h3>
            <label className="form-control">
              <span className="label-text text-xs opacity-70">Paste contract bytecode (0x...)</span>
              <textarea
                className="textarea textarea-bordered font-mono text-xs min-h-[110px]"
                placeholder="0x6080604052348015610010..."
                value={bytecodeInput}
                onChange={(e) => setBytecodeInput(e.target.value)}
              />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={estimateDeployGasFromBytecode}
                disabled={estimateGasLoading}
              >
                {estimateGasLoading ? 'Estimating…' : 'Estimate gas from bytecode'}
              </button>
              {estimateGasStatus && (
                <span
                  className={`text-xs break-all ${
                    estimateGasStatus.startsWith('Estimated ') ? 'text-success' : 'text-warning'
                  }`}
                >
                  {estimateGasStatus}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Gas rows</h2>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}>
              Add row
            </button>
          </div>

          <div className="space-y-2">
            {rows.map((row) => (
              <div key={row.id} className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-2 items-end">
                <label className="form-control">
                  <span className="label-text text-xs opacity-70">Contract / tx label</span>
                  <input
                    type="text"
                    className="input input-bordered input-sm"
                    value={row.label}
                    placeholder="e.g. Factory deploy"
                    onChange={(e) => updateRow(row.id, { label: e.target.value })}
                  />
                </label>
                <label className="form-control">
                  <span className="label-text text-xs opacity-70">Gas units</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="input input-bordered input-sm font-mono"
                    value={row.gasUnits}
                    onChange={(e) => updateRow(row.id, { gasUnits: e.target.value.replace(/[^\d]/g, '') })}
                  />
                </label>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeRow(row.id)}>
                  Remove
                </button>
              </div>
            ))}
          </div>

          <p className="text-sm">
            <span className="opacity-70">Total gas units: </span>
            <span className="font-mono">{totalGasUnits.toLocaleString()}</span>
          </p>
          <p className="text-sm">
            <span className="opacity-70">{brand.gasEstimator.splitterDeployLabel}</span>
            <span className="font-mono">
              {splitterDeployEstimateLoading
                ? 'calculating...'
                : splitterDeployEstimateGas
                  ? Number(splitterDeployEstimateGas).toLocaleString()
                  : '—'}
            </span>
          </p>
        </div>
      </div>

      <div className="card bg-neutral text-neutral-content border border-neutral-700/80">
        <div className="card-body space-y-4">
          <div className="rounded-lg border border-neutral-700/80 bg-base-300/30 p-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {walletChainIcon ? (
                <img src={walletChainIcon} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <span className="h-6 w-6 rounded-full bg-base-300 inline-block" aria-hidden="true" />
              )}
              <span>
                Chain: <span className="font-semibold">{walletChainName}</span>{' '}
                <span className="opacity-70">({sourceChainId ?? '—'})</span>
              </span>
              <span>
                Deploy Wallet:{' '}
                <span className="font-mono">{address ?? 'not connected'}</span>
              </span>
              <span>
                Native balance:{' '}
                <span className="font-mono">
                  {nativeBalance ? `${Number(nativeBalance.formatted).toFixed(6)} ${nativeBalance.symbol}` : '—'}
                </span>
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-semibold">Target chains</h2>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setOpenChains((v) => !v)}>
              {openChains ? 'Hide chain selector' : 'Select chains'}
            </button>
            {openChains && (
              <>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={selectAllChains}
                  disabled={chains.length === 0}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={deselectAllChains}
                  disabled={selectedChainIds.length === 0}
                >
                  Deselect all
                </button>
              </>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={refreshingLive || selectedChains.length === 0}
              onClick={fetchLivePricing}
            >
              {refreshingLive ? 'Refreshing live data…' : 'Fetch live gas + USD'}
            </button>
            <span className="text-xs opacity-70">{selectedChains.length} selected</span>
          </div>

          {chainsLoading && <p className="text-xs opacity-70">Loading LI.FI chains…</p>}
          {chainsError && <p className="text-xs text-error break-all">{chainsError}</p>}
          {liveStatus && <p className="text-xs opacity-70">{liveStatus}</p>}

          {openChains && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {chains.map((chain) => (
                <label key={chain.id} className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={selectedChainIds.includes(chain.id)}
                    onChange={() => toggleChain(chain.id)}
                  />
                  {chain.logoURI ? (
                    <img src={chain.logoURI} alt="" className="w-5 h-5 rounded-full" loading="lazy" />
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-base-300 inline-block" aria-hidden="true" />
                  )}
                  <span className="label-text">{chain.name}</span>
                  <span className="label-text-alt opacity-60">#{chain.id}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card bg-neutral text-neutral-content border border-neutral-700/80">
        <div className="card-body space-y-3">
          <h2 className="font-semibold">Estimated deploy wallet funding</h2>
          <div className="flex flex-wrap gap-3">
            <label className="form-control">
              <span className="label-text text-xs opacity-70">Gas tier</span>
              <select
                className="select select-bordered select-sm"
                value={gasTier}
                onChange={(e) => setGasTier(e.target.value as GasTier)}
              >
                <option value="standard">standard</option>
                <option value="fast">fast</option>
                <option value="fastest">fastest</option>
              </select>
            </label>
          </div>

          <div className="form-control max-w-xs">
            <label className="label py-0">
              <span className="label-text text-xs opacity-70">Buffer multiplier</span>
            </label>
            <input
              type="number"
              min="1"
              step="0.1"
              className="input input-bordered input-sm font-mono"
              value={bufferMultiplierInput}
              onChange={(e) => setBufferMultiplierInput(e.target.value)}
            />
            <label className="label py-1">
              <span className="label-text-alt opacity-70">
                Values above 1 add safety buffer to estimated gas funding (current {bufferMultiplier.toFixed(2)}x).
              </span>
            </label>
          </div>

          {estimates.length === 0 ? (
            <p className="text-sm opacity-70">Select at least one chain.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra table-sm">
                <thead>
                  <tr>
                    <th>Chain</th>
                    <th>Gas price (gwei)</th>
                    <th>Native price (USD)</th>
                    <th>Estimated native needed</th>
                    <th>Estimated USD needed</th>
                    <th>Bridge action</th>
                  </tr>
                </thead>
                <tbody>
                  {estimates.map((estimate) => {
                    const key = String(estimate.chain.id);
                    const { symbol } = gasCurrencyMeta(estimate.chain);
                    const sameAsSource = estimate.chain.id === sourceChainId;
                    const routeState = lifiState[key];

                    return (
                      <tr key={estimate.chain.id}>
                        <td>
                          <div className="flex items-center gap-2">
                            {estimate.chain.logoURI ? (
                              <img src={estimate.chain.logoURI} alt="" className="w-5 h-5 rounded-full" loading="lazy" />
                            ) : (
                              <span className="w-5 h-5 rounded-full bg-base-300 inline-block" aria-hidden="true" />
                            )}
                            <span>{estimate.chain.name}</span>
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            step="any"
                            min="0"
                            className="input input-bordered input-xs w-28 font-mono"
                            value={chainInputs[key]?.gasPriceGwei ?? ''}
                            onChange={(ev) =>
                              setChainInputs((prev) => ({
                                ...prev,
                                [key]: { ...prev[key], gasPriceGwei: ev.target.value },
                              }))
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="any"
                            min="0"
                            className="input input-bordered input-xs w-28 font-mono"
                            value={chainInputs[key]?.nativeUsd ?? ''}
                            onChange={(ev) =>
                              setChainInputs((prev) => ({
                                ...prev,
                                [key]: { ...prev[key], nativeUsd: ev.target.value },
                              }))
                            }
                          />
                        </td>
                        <td className="font-mono">
                          {Number.isFinite(estimate.nativeAmount) ? estimate.nativeAmount.toFixed(6) : '—'} {symbol}
                        </td>
                        <td className="font-mono">{Number.isFinite(estimate.usdAmount) ? fmtUsd(estimate.usdAmount) : '—'}</td>
                        <td>
                          {estimate.chain.lifiSupported === false ? (
                            <span className="text-xs opacity-60">Funding on-chain (not routed via LI.FI)</span>
                          ) : sameAsSource ? (
                            <span className="text-xs opacity-60">Same as source chain</span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                className="btn btn-primary btn-xs"
                                disabled={!isConnected || !address || !sourceChainId || !!routeState?.loading}
                                onClick={() => executeLifiQuote(estimate.chain.id)}
                              >
                                {routeState?.loading ? 'Running…' : 'Review & bridge'}
                              </button>
                              {routeState?.routeId && (
                                <span className="text-[10px] opacity-70">
                                  LI.FI tracking ID: {routeState.routeId.slice(0, 8)}...
                                </span>
                              )}
                              {routeState?.routeSummary && (
                                <span className="text-[10px] opacity-70">Route: {routeState.routeSummary}</span>
                              )}
                              {routeState?.success && (
                                <span className="text-[10px] text-success break-all">{routeState.success}</span>
                              )}
                              {routeState?.error && (
                                <span className="text-[10px] text-error break-all">{routeState.error}</span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={4} className="text-right">
                      Total estimated USD needed
                    </th>
                    <th className="font-mono">{fmtUsd(totalEstimatedUsd)}</th>
                    <th />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <p className="text-xs opacity-70">
            Estimates only: gas markets move by block. Live gas tiers come from LI.FI (
            <a href="https://docs.li.fi/api-reference/gas/get-gas-price-for-the-specified-chainid" className="link">
              wei per gas
            </a>
            , shown as gwei). 
          </p>
          <p className="text-xs opacity-70">
            LI.FI bridge uses native-to-native quotes from your selected source chain. Each destination executes as its own
            route and may require one or more wallet confirmations.
          </p>
        </div>
      </div>

      <div className="flex justify-center">
        <a
          href="https://li.fi"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-xs opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Powered by LI.FI"
        >
          <span>Bridge powered by</span>
          <img
            src={`${process.env.PUBLIC_URL}/logo_lifi_dark_horizontal.png`}
            alt="LI.FI logo"
            className="h-4 w-auto"
            loading="lazy"
          />
        </a>
      </div>

      {pendingRoute && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-lg">Review LI.FI route</h3>
            <p className="text-sm opacity-70 mt-1">
              Destination: {pendingRoute.chainName} · ID: {pendingRoute.route?.id?.slice?.(0, 8) ?? '—'}...
            </p>
            <p className="text-sm mt-2">
              You send ~
              {formatTokenAmount(
                pendingRoute.route?.fromAmount ?? pendingRoute.route?.steps?.[0]?.action?.fromAmount,
                pendingRoute.route?.fromToken?.decimals ?? pendingRoute.route?.steps?.[0]?.action?.fromToken?.decimals
              )}{' '}
              {pendingRoute.route?.fromToken?.symbol ?? pendingRoute.route?.steps?.[0]?.action?.fromToken?.symbol ?? 'source native'}{' '}
              to receive ~
              {formatTokenAmount(
                pendingRoute.route?.toAmount ??
                  pendingRoute.route?.steps?.[pendingRoute.route?.steps?.length - 1]?.estimate?.toAmount ??
                  pendingRoute.route?.steps?.[pendingRoute.route?.steps?.length - 1]?.estimate?.toAmountMin,
                pendingRoute.route?.toToken?.decimals ??
                  pendingRoute.route?.steps?.[pendingRoute.route?.steps?.length - 1]?.estimate?.toToken?.decimals
              )}{' '}
              {pendingRoute.route?.toToken?.symbol ??
                pendingRoute.route?.steps?.[pendingRoute.route?.steps?.length - 1]?.estimate?.toToken?.symbol ??
                'destination native'}
            </p>
            <div className="mt-3 space-y-2 max-h-80 overflow-auto">
              {(pendingRoute.route?.steps ?? []).map((step: any, idx: number) => (
                <div key={step?.id ?? idx} className="rounded border border-neutral-700/70 p-2 text-sm">
                  <div className="font-medium">
                    Step {idx + 1}: {step?.toolDetails?.name ?? step?.tool ?? step?.type ?? 'Route step'}
                  </div>
                  <div className="opacity-70 text-xs">
                    Type: {step?.type ?? '—'} · {step?.action?.fromChainId ?? '?'} {'->'}{' '}
                    {step?.action?.toChainId ?? '?'}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs opacity-70 mt-3">
              Confirm to continue with wallet transactions for this route.
            </p>
            <div className="modal-action">
              <button type="button" className="btn btn-ghost" onClick={() => setPendingRoute(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => executePreparedRoute(pendingRoute.chainId, pendingRoute.route)}
              >
                Proceed to wallet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
